'use client';

import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface IProps {
  src: string;
  alt: string;
  width: number | `${number}` | undefined;
  height: number | `${number}` | undefined;
  fit: 'cover' | 'contain';
  preload?: boolean;
  className?: string;
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  onLoad?: () => void;
  onClick?: () => void;
  useSekelton?: boolean;
}

export interface ILazyImageHandle {}

const LazyImage = React.forwardRef<ILazyImageHandle, IProps>((props, ref) => {
  const { ref: inViewRef, inView } = useInView({ threshold: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [actualSrc, setActualSrc] = useState<string>();

  useImperativeHandle(ref, () => ({ ref: inViewRef }), [inViewRef]);

  // ===== 換圖後，設置狀態為需要重新載入 =====
  // 時機: 前一個圖片路徑和現在的不同
  const [needReload, setNeedReload] = useState(false);
  const [prevSrc, setPrevSrc] = useState<string>(props.src);
  useEffect(() => {
    if (props.src !== prevSrc) {
      setNeedReload(true);
      setPrevSrc(props.src);
    }
  }, [props.src, prevSrc]);

  // ===== 定義必要函數 =====
  // ----- 載入圖片 -----
  const loadImage = useCallback(() => {
    setActualSrc(props.src);
  }, [props.src]);

  // ===== 載入圖片 =====
  useEffect(() => {
    // 初次載入
    if (!isLoaded) {
      // 自動載入
      if (props.preload) loadImage();

      // 進入視圖載入
      if (inView) loadImage();
    }
    // 需要重新載入
    else if (needReload) {
      // 自動載入
      if (props.preload) loadImage();

      // 進入視圖載入
      if (inView) loadImage();
    }
    // 其他情況(已載入過且不需重新載入)，不做動作
    else {
      //
    }
  }, [props.src, inView, props.preload, needReload, isLoaded, loadImage]);

  return (
    <div
      className={clsx('overflow-hidden select-none', props.className)}
      style={props.wrapperStyle}
      ref={inViewRef}
      onClick={props.onClick}
    >
      {/* 實際圖片 */}
      <img
        src={actualSrc}
        alt={props.alt}
        width={props.width}
        height={props.height}
        draggable={false}
        className={clsx(
          'w-full h-full pointer-events-none',
          { 'object-cover': props.fit === 'cover' },
          { 'object-contain': props.fit === 'contain' },
          { 'transition-opacity duration-300': true },
          { 'opacity-0': !isLoaded },
          'data-[loaded=false]:animate-pulse data-[loaded=false]:bg-gray-100/10'
        )}
        style={props.style}
        onLoad={event => {
          setIsLoaded(true);

          props.onLoad && props.onLoad();

          event.currentTarget.setAttribute('data-loaded', 'true');

          setNeedReload(false);
        }}
      />
    </div>
  );
});
LazyImage.displayName = 'LazyImage';

export default LazyImage;
